/**
 * Process Visualization Widget
 * Main widget class that coordinates all components for embeddable process visualization
 */
import { ConfigurationManager } from './configurationManager.js';
import { DataSourceManager } from './dataSourceManager.js';
import { UIBuilder } from './uiBuilder.js';
import { GraphVisualizer } from './graphVisualizer.js';
import { processAnalyzer } from './processAnalyzer.js';

export class ProcessVisualizationWidget {
    constructor(containerId, userConfig = {}) {
        this.containerId = containerId;
        this.container = typeof containerId === 'string' ? document.getElementById(containerId) : containerId;
        
        if (!this.container) {
            throw new Error(`Container element with ID '${containerId}' not found`);
        }

        // Initialize configuration manager
        this.configManager = new ConfigurationManager(userConfig);
        this.config = this.configManager.getAll();

        // Validate configuration
        const validation = this.configManager.validate();
        if (!validation.isValid) {
            throw new Error(`Configuration validation failed: ${validation.errors.join(', ')}`);
        }

        // Initialize components
        this.dataSourceManager = new DataSourceManager(this.config);
        this.uiBuilder = new UIBuilder(this.container, this.config);
        this.eventEmitter = new EventEmitter();
        
        // State
        this.currentData = null;
        this.graphVisualizer = null;
        this.isInitialized = false;
        this.isLoading = false;

        // Bind methods to preserve context
        this.handleNodeClick = this.handleNodeClick.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleZoomChange = this.handleZoomChange.bind(this);
        this.handleError = this.handleError.bind(this);
    }

    /**
     * Initialize the widget
     */
    async init() {
        if (this.isInitialized) {
            return;
        }

        try {
            // Build UI
            this.uiElements = this.uiBuilder.buildInterface();
            
            // Initialize graph visualizer with configuration
            this.initializeGraphVisualizer();
            
            // Set up event listeners
            this.setupEventListeners();
            
            // Load initial data if auto-load is enabled
            if (this.config.dataSource.autoLoad !== false) {
                await this.loadData();
            }

            this.isInitialized = true;
            this.emit('initialized');
            
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Initialize the graph visualizer with current configuration
     */
    initializeGraphVisualizer() {
        // Create a new GraphVisualizer instance with the correct container ID
        const graphContainerId = `graph-container-${this.containerId}`;
        this.graphVisualizer = new GraphVisualizer(graphContainerId);
        
        // Override default colors and settings with configuration
        this.graphVisualizer.nodeColors = this.config.visualization.nodeColors;
        this.graphVisualizer.edgeColors = this.config.visualization.edgeColors;
        this.graphVisualizer.cytoscapeOptions = this.config.visualization.cytoscape;
        
        // Don't initialize immediately - wait until we have data to render
        // This prevents issues with container not being ready
    }

    /**
     * Ensure graph visualizer is initialized when needed
     */
    ensureGraphVisualizerInitialized() {
        return new Promise((resolve, reject) => {
        console.log('ensureGraphVisualizerInitialized called');
            if (this.graphVisualizer && this.graphVisualizer.cy) {
                console.log('GraphVisualizer already initialized');
                resolve();
                return;
            }
    
            if (!this.graphVisualizer) {
                console.error('GraphVisualizer instance does not exist');
                reject(new Error('GraphVisualizer instance does not exist'));
                return;
            }
    
            const graphContainerId = `graph-container-${this.containerId}`;
            const container = document.getElementById(graphContainerId);
    
            if (!container) {
                console.error(`Graph container with ID '${graphContainerId}' not found.`);
                reject(new Error(`Graph container with ID '${graphContainerId}' not found.`));
                return;
            }
    
            const checkDimensions = () => {
                if (container.offsetWidth > 0 && container.offsetHeight > 0) {
                    console.log('Container is ready. Initializing GraphVisualizer...');
                    this.graphVisualizer.init();
                    
                    console.log('After init - graphVisualizer.cy exists:', !!this.graphVisualizer.cy);
                    
                    // Override the default layout configuration if initialization succeeded
                    if (this.graphVisualizer.cy) {
                        resolve();
                    } else {
                        reject(new Error('GraphVisualizer initialization failed'));
                    }
                } else if (this.graphVisualizer && this.graphVisualizer.cy) {
                    console.log('GraphVisualizer already initialized');
                } else {
                    console.log('Container not ready, waiting...');
                    requestAnimationFrame(checkDimensions);
                }
            };
    
            checkDimensions();
        });
    }

    /**
     * Set up event listeners for UI interactions
     */
    setupEventListeners() {
        // Filter controls
        if (this.config.ui.showFilters) {
            const edgeFilter = document.getElementById(`edge-filter-${this.containerId}`);
            const nodeFilter = document.getElementById(`node-filter-${this.containerId}`);
            
            if (edgeFilter) {
                edgeFilter.addEventListener('input', this.handleFilterChange);
            }
            
            if (nodeFilter) {
                nodeFilter.addEventListener('input', this.handleFilterChange);
            }
        }

        // Zoom controls
        if (this.config.ui.showZoomControls) {
            const zoomIn = document.getElementById(`zoom-in-${this.containerId}`);
            const zoomOut = document.getElementById(`zoom-out-${this.containerId}`);
            
            if (zoomIn) {
                zoomIn.addEventListener('click', () => this.zoomIn());
            }
            
            if (zoomOut) {
                zoomOut.addEventListener('click', () => this.zoomOut());
            }
        }

        // Window resize
        window.addEventListener('resize', () => { if (this.graphVisualizer) {
                this.graphVisualizer.resize();
            }
        });

        // Graph visualizer events (if available)
        if (this.graphVisualizer && this.graphVisualizer.cy) {
            this.graphVisualizer.cy.on('tap', 'node', (evt) => {
                this.handleNodeClick(evt.target);
            });
        }
    }

    /**
     * Load data from the configured data source
     */
    async loadData() {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.uiBuilder.showLoading();

        try {
            // Fetch data from data source
            const rawData = await this.dataSourceManager.fetchData();

            console.log("GO TO ANALYZING")
            // Process data using the analyzer
            const processData = processAnalyzer.analyze(rawData);
            console.log("ANALYZING DONE")

            // Store processed data
            this.currentData = processData;
            
            // Ensure graph visualizer is initialized before rendering
            await this.ensureGraphVisualizerInitialized();
            
            // Visualize the data
            if (this.graphVisualizer && this.graphVisualizer.cy) {
                await this.graphVisualizer.renderGraph(processData);
            }
            
            // Show success message
            if (this.config.messages.success !== undefined && this.config.messages.success !== '') {
                this.uiBuilder.showMessage(this.config.messages.success, 'success');
            }

            // Call user-provided callback if available
            if (this.config.callbacks.onDataLoad) {
                this.config.callbacks.onDataLoad(processData);
            }
            
            // Emit data loaded event
            this.emit('dataLoaded', processData);
            
        } catch (error) {
            this.handleError(error);
        } finally {
            this.isLoading = false;
            this.uiBuilder.hideLoading();
        }
    }

    /**
     * Handle node click events
     */
    handleNodeClick(node) {
        const nodeData = node.data();
        
        // Call user-provided callback if available
        if (this.config.callbacks.onNodeClick) {
            this.config.callbacks.onNodeClick(nodeData);
        }
        
        // Emit event for external listeners
        this.emit('nodeClick', nodeData);
        
        // Update metrics panel if available
        if (this.graphVisualizer) {
            this.graphVisualizer.updateMetricsPanel(node);
        }
    }

    /**
     * Handle filter change events
     */
    handleFilterChange(event) {
        if (!this.graphVisualizer) return;

        const filterId = event.target.id;
        const value = parseInt(event.target.value, 10);
        
        // Update filter value display
        const valueDisplay = document.getElementById(`${filterId}-value`);
        if (valueDisplay) {
            valueDisplay.textContent = `${value}%`;
        }
        
        // Update graph visualizer filters
        if (filterId.includes('edge-filter')) {
            this.graphVisualizer.edgeFilterPercentage = value;
        } else if (filterId.includes('node-filter')) {
            this.graphVisualizer.nodeFilterPercentage = value;
        }
        
        // Apply filters
        this.graphVisualizer.applyFilters();
        
        // Call user-provided callback if available
        if (this.config.callbacks.onFilterChange) {
            this.config.callbacks.onFilterChange({
                type: filterId.includes('edge-filter') ? 'edge' : 'node',
                value: value
            });
        }
        
        // Emit event
        this.emit('filterChange', {
            type: filterId.includes('edge-filter') ? 'edge' : 'node',
            value: value
        });
    }

    /**
     * Handle zoom change events
     */
    handleZoomChange(zoomLevel) {
        // Call user-provided callback if available
        if (this.config.callbacks.onZoomChange) {
            this.config.callbacks.onZoomChange(zoomLevel);
        }
        
        // Emit event
        this.emit('zoomChange', zoomLevel);
    }

    /**
     * Handle errors
     */
    handleError(error) {
        console.error('ProcessVisualizationWidget Error:', error);
        
        // Show error message
        if (this.config.messages.error && this.config.messages.error !== '') {
            this.uiBuilder.showMessage(`${this.config.messages.error}: ${error.message}`, 'error');
        }

        // Call user-provided callback if available
        if (this.config.callbacks.onDataError) {
            this.config.callbacks.onDataError(error);
        }
        
        // Emit error event
        this.emit('error', error);
    }

    /**
     * Zoom in
     */
    zoomIn() {
        if (this.graphVisualizer && this.graphVisualizer.cy) {
            const currentZoom = this.graphVisualizer.cy.zoom();
            const newZoom = currentZoom * 1.2;
            
            this.graphVisualizer.cy.zoom({
                level: newZoom,
                renderedPosition: { 
                    x: this.graphVisualizer.cy.width() / 2, 
                    y: this.graphVisualizer.cy.height() / 2 
                }
            });
            
            this.handleZoomChange(newZoom);
        }
    }

    /**
     * Zoom out
     */
    zoomOut() {
        if (this.graphVisualizer && this.graphVisualizer.cy) {
            const currentZoom = this.graphVisualizer.cy.zoom();
            const newZoom = currentZoom * 0.8;
            
            this.graphVisualizer.cy.zoom({
                level: newZoom,
                renderedPosition: { 
                    x: this.graphVisualizer.cy.width() / 2, 
                    y: this.graphVisualizer.cy.height() / 2 
                }
            });
            
            this.handleZoomChange(newZoom);
        }
    }

    /**
     * Update widget configuration
     */
    updateConfig(newConfig) {
        this.configManager.update(newConfig);
        this.config = this.configManager.getAll();
        
        // Update components
        this.dataSourceManager.updateConfig(this.config);
        this.uiBuilder.updateConfig(this.config);
        
        // Reinitialize graph visualizer if visualization config changed
        if (newConfig.visualization) {
            this.initializeGraphVisualizer();
        }
        
        // Emit config change event
        this.emit('configChanged', this.config);
    }

    /**
     * Set data directly (for static data source)
     */
    setData(data) {
        this.configManager.set('dataSource.type', 'static');
        this.configManager.set('dataSource.static.data', data);
        this.config = this.configManager.getAll();
        this.dataSourceManager.updateConfig(this.config);
        
        // Reload data
        this.loadData();
    }

    /**
     * Get current data
     */
    getData() {
        return this.currentData;
    }

    /**
     * Destroy the widget and clean up resources
     */
    destroy() {
        // Remove event listeners
        
        // Clean up graph visualizer
        if (this.graphVisualizer && this.graphVisualizer.cy) {
            this.graphVisualizer.cy.destroy();
        }
        
        // Clear container
        this.container.innerHTML = '';
        this.container.classList.remove(this.config.ui.containerClass);
        
        // Reset state
        this.isInitialized = false;
        this.currentData = null;
        
        // Emit destroyed event
        this.emit('destroyed');
    }

    /**
     * Event emitter methods
     */
    on(event, callback) {
        this.eventEmitter.on(event, callback);
    }

    /**
     * Debounce utility function
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func.apply(this, args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    off(event, callback) {
        this.eventEmitter.off(event, callback);
    }

    emit(event, data) {
        this.eventEmitter.emit(event, data);
    }
}

/**
 * Simple Event Emitter for widget events
 */
export class EventEmitter {
    constructor() {
        this.events = {};
    }

    on(event, callback) {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(callback);
    }

    off(event, callback) {
        if (!this.events[event]) return;
        
        const index = this.events[event].indexOf(callback);
        if (index > -1) {
            this.events[event].splice(index, 1);
        }
    }

    emit(event, data) {
        if (!this.events[event]) return;
        
        this.events[event].forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                console.error(`Error in event callback for '${event}':`, error);
            }
        });
    }
}